import uniqid from 'uniqid'

/** UTILS */
import { getArrayOfKeyValueObjects } from '~/utils/getArrayOfKeyValueObjects'
import isClientSide from '~/utils/isClientSide'
import preparedGALink from '~/utils/ga'

/** QUERIES */
import { SEND_EVENT } from '@/queries/PROMETHEUS/sendEvent'

/** ENUMS */
import ECookies from '@/ts/enums/ECookies'
import EStorage from '@/ts/enums/EStorage'

/** INTERFACES */
import { useServerLogger } from '~/composables/useServerLogger'
import { stats } from '~/utils/stats'
import type { EventInput, UniqueInfo } from '~/schema/types/generated'

export function useSendEvent() {
    const config = useRuntimeConfig()
    const url = config.public.api.prometheus ?? '/api/hermes/graphql'

    const sendEvent = async (
        {
            category,
            action = 'click',
            label = '',
            value = '0',
            click_text = '',
            click_path = '',
            click_element = 'link',
            component_name = '',
            offer = '',
        }: {
            category?: string
            action?: string
            label?: string | undefined
            value?: string
            click_text?: string
            click_path?: string
            click_element?: string
            component_name: string
            offer?: string
        },
        eventInfo?: { [key: string]: string },
        otherInfo?: UniqueInfo[],
    ): Promise<void> => {
        const route = useRoute()
        const routeName = route.path.substring(1)
        const defaultCategory = routeName.length ? `lp-${routeName}` : 'lp-homepage'
        const eventCategory = category || defaultCategory

        const GA = {
            category: eventCategory,
            action,
            label,
            value,
        }
        const deviceData = eventInfo
        const unique_info = getArrayOfKeyValueObjects({
            objectToConvert: deviceData || {},
            additionalElements: otherInfo || [],
        })

        const data: EventInput = {
            version: '1.0',
            event_type: 'event',
            experiments_info: [
                {
                    experiment_id: '',
                    experiment_variant: '',
                },
            ],
            ga_attributes: GA,
            ids: {
                event_id: uniqid(),
                mouse_position: '',
                pageview_id: sessionStorage.getItem(EStorage.PAGEVIEW_ID)! || '',
                session_id: sessionStorage.getItem(EStorage.SESSION_ID)! || '',
                user_id: useCookie(ECookies.USER_ID)?.value || '',
            },
            timestamp: new Date().toISOString(),
            unique_info,
        }

        if (isClientSide()) {
            stats.handleGAEvent({
                category: eventCategory,
                action,
                label,
                click_text,
                click_path,
                click_element,
                component_name,
                offer,
            })
            try {
                useServerLogger().dataLogger(
                    '(SEND_EVENT) ACTION: sendEvent ',
                    `Send event type:${action},Event: ${JSON.stringify(GA)}`,
                )
                await $fetch(url, {
                    method: 'POST',
                    body: {
                        query: SEND_EVENT.loc?.source.body,
                        variables: { event: data },
                    },
                })
            } catch (error) {
                console.error('sendEvent error', error)
                useServerLogger().dataLogger(
                    '(SEND_EVENT) ERROR: sendEvent ',
                    `ERROR from send event type:${action},Event: ${JSON.stringify(GA)}`,
                )
            }
        }
    }

    const sendMenuEvent = async (label: string, url: string = '') => {
        let action: string = 'click'
        if (url) {
            action += `_${preparedGALink(url)}`
        }
        sendEvent({
            category: 'lp-homepage_main_menu',
            action,
            label,
            click_text: '',
            click_path: '',
            click_element: '',
            component_name: '',
            offer: '',
        })
    }

    return {
        sendEvent,
        sendMenuEvent,
    }
}
